import "../styles/Common.css";
import "../styles/Task.css";

import React from "react";

import { mergeStyles } from "@fluentui/react";
import { Button, Image, Spinner, Text, MessageBar, MessageBarBody, MessageBarTitle } from "@fluentui/react-components";
import {
  Search20Filled,
  Search20Regular,
  DismissRegular,
} from "@fluentui/react-icons";
import { BaseWidget, IWidgetClassNames } from "@microsoft/teamsfx-react";

import { EmptyThemeImg } from "../components/EmptyThemeImg";
import { TeamsFxContext } from "../internal/context";
import { TeamModel } from "../models/teamModel";
import { ErrorCode, TeamJoinError } from "../models/errorModel";
import { getTeams, joinTeam, getJoinedTeams, getBatchIcons, getCachedIcons } from "../services/teamService";

import { app } from "@microsoft/teams-js";

interface ITeamState {
  teams?: TeamModel[];
  filteredTeams?: TeamModel[];
  joinedTeams?: TeamModel[];
  filter?: string;
  showAll?: boolean;
  inputFocused?: boolean;
  addBtnOver?: boolean;
  addBtnClicked?: boolean;
  partiallyLoaded?: boolean;
  notification?: {
    intent: "success" | "error" | "warning" | "info";
    title: string;
    message?: string;
  } | null;
}

export class Team extends BaseWidget<any, ITeamState> {
  inputDivRef;
  btnRef;
  inputRef;

  constructor(props: any) {
    super(props);
    this.inputRef = React.createRef<HTMLInputElement>();
    this.inputDivRef = React.createRef<HTMLDivElement>();
    this.btnRef = React.createRef<HTMLButtonElement>();
  }

  override async getData(): Promise<ITeamState> {
    const data = await this.loadData();
    // Seed icons that are already cached synchronously, so they're part of the
    // initial state. (If applied asynchronously they'd race — and lose to — the
    // widget's own setState of the freshly-loaded, icon-less data on reload.)
    const ids = data.teams.map((t) => t.officeGroupId).filter(Boolean) as string[];
    const cached = getCachedIcons(ids);
    const withCached = data.teams.map((t) => ({ ...t, icon: cached[t.officeGroupId] ?? t.icon }));
    data.teams = withCached;
    data.filteredTeams = withCached;
    // Fetch any not-yet-cached icons in the background (batched), so the team list
    // and Join buttons render immediately and are never blocked by icon fetches.
    void this.loadIcons(withCached);
    return data;
  }

  /**
   * Resolves all team icons in one batched, cached request and applies them in a
   * single state update. Failures are swallowed (the UI falls back to the icon
   * placeholder), so a slow or failing icon never impacts core actions like Join.
   */
  private async loadIcons(teams: TeamModel[]): Promise<void> {
    try {
      const ids = teams.map((t) => t.officeGroupId).filter(Boolean) as string[];
      if (ids.length === 0) return;

      const iconMap = await getBatchIcons(ids);

      const applyIcons = (arr?: TeamModel[]) =>
        (arr ?? teams).map((t) => ({ ...t, icon: iconMap[t.officeGroupId] ?? t.icon }));

      this.setState((prev) => ({
        teams: applyIcons(prev.teams),
        filteredTeams: applyIcons(prev.filteredTeams),
      }));
    } catch (e) {
      console.error("Failed to load team icons", e);
    }
  }

  override componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<ITeamState>, snapshot?: any): void {
    if (!this.state.teams) 
      return;

    if (this.state.filter !== prevState.filter) {
      const filteredTeams = this.state.teams?.filter(
        (team) => this.teamMatchesFilter(team, this.state.filter));
      this.setState({ filteredTeams });
    }
  }

  private teamMatchesFilter(team: TeamModel, filter: string): boolean {
    if (!filter) {
      return true;
    }
    const lowerFilter = filter.toLowerCase();
    if (team.name && team.name.toLowerCase().includes(lowerFilter)) {
      return true;
    }
    if (team.description && team.description?.toLowerCase().includes(lowerFilter)) {
      return true;
    }
    return false;
  }

  async loadData(): Promise<ITeamState> {
    try {
      const teamsPromise = getTeams(true).then((z) => {this.setState( {partiallyLoaded : true}); return z;});
      const joinedTeamsPromise = getJoinedTeams().then((z) => {this.setState( {partiallyLoaded : true}); return z;});
      let [teams, joinedTeams] = await Promise.all([teamsPromise, joinedTeamsPromise]); 
      return { teams, joinedTeams, filteredTeams: teams, inputFocused: false, addBtnOver: false };
    } catch (error) {
      console.error(error);
      return { teams: [], joinedTeams: [], filteredTeams: [], inputFocused: false, addBtnOver: false };
    } finally {
      this.setState({ loading: false });
    }
  }

  override header(): JSX.Element | undefined {
    return (
      <div>
        <TeamsFxContext.Consumer>
          {({ themeString }) =>
            themeString === "default" ? <Image src={`teams.svg`} /> : <Image src={`teams.svg`} />
          }
        </TeamsFxContext.Consumer>
      </div>
    );
  }

  private showNotification = (intent: "success" | "error" | "warning" | "info", title: string, message?: string) => {
    this.setState({ notification: { intent, title, message } });
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      this.setState({ notification: null });
    }, 5000);
  };

  private dismissNotification = () => {
    this.setState({ notification: null });
  };

  override body(): JSX.Element | undefined {
    const hasTeams = this.state.teams?.length !== 0;

    return (
      <div className={hasTeams ? "has-task-layout" : ""}>
        {this.state.notification && (
          <MessageBar intent={this.state.notification.intent} style={{ marginBottom: '12px' }}>
            <MessageBarBody>
              <MessageBarTitle>{this.state.notification.title}</MessageBarTitle>
              {this.state.notification.message}
            </MessageBarBody>
            <Button
              onClick={this.dismissNotification}
              appearance="transparent"
              icon={<DismissRegular />}
              size="small"
            />
          </MessageBar>
        )}
        {this.inputLayout()}
        {hasTeams ? (
          this.state.filteredTeams?.map((item: TeamModel) => {
            const isJoined = this.state.joinedTeams?.findIndex((team) => team.id === item.officeGroupId) !== -1;
            const hasIcon = item.icon !== undefined;
            return (
              <div key={`div-task-item-${item.officeGroupId}`} className="task-item">
                { hasIcon ? <img className="icon" src={item.icon} alt="icon" /> : <div className="icon-placeholder"></div> }
                {item.name}

                { isJoined ?
                  <Button name="Go" appearance="primary" onClick={async (event) => {
                    try {
                      await app.openLink(item.url ?? "");
                    } catch (error) {
                      console.error(error);
                      this.showNotification("error", "Failed to open team", "Unable to navigate to the team page.");
                    }
                  }} >Go</Button> :
                  <Button name="Join" onClick={async (event) => {
                    let target = event.currentTarget as HTMLButtonElement;
                    target.disabled = true;
                    target.textContent = "Joining…";

                    try {
                      const result = await joinTeam(item.officeGroupId);

                      if (result && result.teamLink) {
                        this.showNotification("success", "Successfully joined!", `You're now a member of ${item.name}.`);

                        try {
                          await app.openLink(result.teamLink ?? "");
                          target.textContent = "Go";
                          // Update the joinedTeams list - add the current team with its webUrl
                          const updatedTeam: TeamModel = {
                            ...item,
                            url: result.teamLink
                          };
                          this.setState({
                            joinedTeams: [...(this.state.joinedTeams || []), updatedTeam]
                          });
                        } catch (error) {
                          console.error(error);
                          this.showNotification("warning", "Joined successfully", "But couldn't open the team page. You can find it in your Teams list.");
                          target.textContent = "Go";
                        }
                      } else {
                        target.textContent = "Join";
                        this.showNotification("error", "Failed to join team", "No team link was returned. Please try again.");
                      }
                    } catch (error) {
                      target.disabled = false;
                      target.textContent = "Join";

                      if (error instanceof TeamJoinError) {
                        // Handle specific error types
                        switch (error.code) {
                          case ErrorCode.TIMEOUT:
                            this.showNotification("warning", "Request timed out", error.message);
                            break;
                          case ErrorCode.PERMISSION_DENIED:
                            this.showNotification("error", "Access denied", error.message);
                            break;
                          case ErrorCode.ALREADY_MEMBER:
                            this.showNotification("info", "Already a member", error.message);
                            // Try to open the team if already a member
                            if (item.url) {
                              try {
                                await app.openLink(item.url);
                                target.textContent = "Go";
                              } catch {}
                            }
                            break;
                          case ErrorCode.TEAM_NOT_FOUND:
                            this.showNotification("error", "Team not found", error.message);
                            break;
                          case ErrorCode.NETWORK_ERROR:
                            this.showNotification("error", "Network error", error.message);
                            break;
                          default:
                            this.showNotification("error", "Something went wrong", error.message);
                            console.error("Join team error:", error.details);
                        }
                      } else {
                        this.showNotification("error", "Unexpected error", "An unexpected error occurred. Please try again.");
                        console.error(error);
                      }
                    }
                  }}>Join</Button>
                  }
              </div>
            );
          })
        ) : (
          <div className="empty-layout">
            <EmptyThemeImg />
            <Text>No teams are available, when they are you'll find them here</Text>
          </div>
        )}
      </div>
    );
  }



  override footer(): JSX.Element | undefined {
    return undefined;
  }

    // This function returns the JSX for the input layout
    private inputLayout(): JSX.Element | undefined {
      return (
        <div
          ref={this.inputDivRef}
          className={mergeStyles(
            "add-task",
            this.state.inputFocused ? "focused-color" : "non-focused-color"
          )}
        >
          {this.state.inputFocused ? (
            <Search20Filled className="add-btn" />
          ) : (
            <Search20Regular className="add-btn" />
          )}
  
          <input autoFocus
            ref={this.inputRef}
            type="text"
            className={mergeStyles(
              "input",
              this.state.inputFocused ? "focused-color" : "non-focused-color"
            )}
            onFocus={() => this.setState({ inputFocused: true })}
            onBlur={() => this.setState({ inputFocused: this.state.filter !== "" })}
            onKeyUp={() => this.setState({ filter: this.inputRef.current?.value })}
            placeholder="Search for team"
          />
        </div>
      );
    }
  
  override styling(): IWidgetClassNames {
    return { root: "concise-root", footer: "footer-btn" };
  }

  override loading(): JSX.Element | undefined {
    return (
      <div className="loading-layout">
        <Spinner label={this.state.partiallyLoaded ? "Almost done…" : "Loading…"} labelPosition="below" />
      </div>
    );
  }



}
